export const GOOGLE_ADS_ATTRIBUTION_STORAGE_KEY =
  'todesktop.googleAdsAttribution';

const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
const MAX_CLICK_ID_LENGTH = 500;

export interface GoogleAdsAttribution {
  capturedAt: string;
  gbraid?: string;
  gclid?: string;
  wbraid?: string;
}

function sanitizeClickId(value: string | null): string | undefined {
  const normalized = value?.trim();
  if (!normalized || normalized.length > MAX_CLICK_ID_LENGTH) {
    return undefined;
  }
  return normalized;
}

function normalizeAttribution(
  value: Partial<GoogleAdsAttribution>,
): GoogleAdsAttribution | undefined {
  const capturedAt = new Date(value.capturedAt ?? '');
  if (
    Number.isNaN(capturedAt.getTime()) ||
    capturedAt.getTime() - Date.now() > 5 * 60 * 1000 ||
    Date.now() - capturedAt.getTime() > ATTRIBUTION_TTL_MS
  ) {
    return undefined;
  }

  const attribution: GoogleAdsAttribution = {
    capturedAt: capturedAt.toISOString(),
  };
  for (const key of CLICK_ID_KEYS) {
    const clickId = sanitizeClickId(value[key] ?? null);
    if (clickId) attribution[key] = clickId;
  }

  return CLICK_ID_KEYS.some((key) => attribution[key])
    ? attribution
    : undefined;
}

export function captureGoogleAdsAttribution(
  search = window.location.search,
): GoogleAdsAttribution | undefined {
  const searchParams = new URLSearchParams(search);
  const attribution = normalizeAttribution({
    capturedAt: new Date().toISOString(),
    gbraid: searchParams.get('gbraid') ?? undefined,
    gclid: searchParams.get('gclid') ?? undefined,
    wbraid: searchParams.get('wbraid') ?? undefined,
  });

  if (attribution) {
    try {
      window.localStorage.setItem(
        GOOGLE_ADS_ATTRIBUTION_STORAGE_KEY,
        JSON.stringify(attribution),
      );
    } catch {
      // Checkout still works when browser storage is unavailable.
    }
  }

  return attribution;
}

export function getGoogleAdsAttribution(): GoogleAdsAttribution | undefined {
  try {
    const stored = window.localStorage.getItem(
      GOOGLE_ADS_ATTRIBUTION_STORAGE_KEY,
    );
    if (!stored) return undefined;

    const attribution = normalizeAttribution(JSON.parse(stored));
    if (!attribution) {
      window.localStorage.removeItem(GOOGLE_ADS_ATTRIBUTION_STORAGE_KEY);
    }
    return attribution;
  } catch {
    return undefined;
  }
}
